import type { ConnectedProjectDto } from "@/api/types";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { FormField } from "@/design-system/FormField";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

import { filterProjectsByFilters } from "./filterProjectsByFilters";
import { ProjectFilterDrawerFields } from "./ProjectFilterDrawerFields";

export type PortfolioProjectFilterValues = {
  owner?: string[];
  maintenance?: string[];
  city?: string[];
  projectIds?: string[];
};

export const CLEARED_PORTFOLIO_PROJECT_FILTER: PortfolioProjectFilterValues = {
  owner: undefined,
  maintenance: undefined,
  city: undefined,
  projectIds: undefined,
};

export function PortfolioProjectFilterFields({
  projects,
  value,
  onChange,
  includeProjectSelection = false,
}: {
  projects: ConnectedProjectDto[];
  value: PortfolioProjectFilterValues;
  onChange: (value: PortfolioProjectFilterValues) => void;
  includeProjectSelection?: boolean;
}): React.ReactNode {
  const { t } = useTranslation();
  const filteredProjects = filterProjectsByFilters(projects, {
    owner: value.owner,
    maintenance: value.maintenance,
    city: value.city,
  });

  return (
    <Stack gap="md">
      <ProjectFilterDrawerFields
        projects={projects}
        filters={value}
        onChange={(updated) => onChange(updatePortfolioProjectFilters(projects, value, updated))}
      />
      {includeProjectSelection && filteredProjects.length > 1 && (
        <FormField label={t("page.portfolio.tickets.filter.project.label")}>
          <CheckboxMultiSelect
            renderMoreSelected={(count) => t("common.label.x-more", { count })}
            selected={filteredProjects.filter((project) => value.projectIds?.includes(project.id))}
            items={filteredProjects}
            keySelector={(project) => project.id}
            renderOption={(project) => project.name}
            onChange={(selectedProjects) =>
              onChange({ ...value, projectIds: selectedProjects.map((project) => project.id) })
            }
            allSelectedText={t("page.tickets.header.filter.projects.all")}
            placeholder={t("page.tickets.header.filter.projects")}
            searchableFieldSelector={(project) => [project.name, project.city]}
          />
        </FormField>
      )}
    </Stack>
  );
}

export function updatePortfolioProjectFilters(
  projects: ConnectedProjectDto[],
  value: PortfolioProjectFilterValues,
  updated: PortfolioProjectFilterValues,
): PortfolioProjectFilterValues {
  const nextFilters = {
    ...value,
    owner: updated.owner ?? [],
    maintenance: updated.maintenance ?? [],
    city: updated.city ?? [],
  };
  const visibleProjectIds = new Set(filterProjectsByFilters(projects, nextFilters).map((project) => project.id));
  const projectIds = value.projectIds?.filter((projectId) => visibleProjectIds.has(projectId));

  return {
    ...nextFilters,
    projectIds: projectIds?.length ? projectIds : undefined,
  };
}
